import { useState } from "react";
import { faAngleRight, faArrowsRepeat } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";
import { pluralized } from "common/utils/strings";

import Switch from "client/common/components/inputs/Switch";
import Box from "client/common/components/layouts/Box";
import Address from "client/common/components/content/Address";
import TextChip from "client/common/components/content/TextChip";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useRemoveAddressDrawer } from "client/accounts/stackables";

import { useClientQuery, useClientUserQuery, useUpdateClientMutation } from "../../hooks";
import Tooltip from "../../../common/components/content/Tooltip";

import styles from "../../styles/UpdateAddressForm.module.less";

export type AccountUpdateAddressDrawerProps = {
  id: number;
};

const AccountUpdateAddressDrawer = ({ id }: AccountUpdateAddressDrawerProps) => {
  const { data: client, isLoading: isClientLoading, error: clientError } = useClientQuery();
  const {
    data: clientUser,
    isLoading: isClientUserLoading,
    error: clientUserError,
  } = useClientUserQuery();
  const {
    mutate: updateClient,
    isLoading: isUpdating,
    error: updateClientError,
  } = useUpdateClientMutation();
  const { open: openRemoveAddressDrawer } = useRemoveAddressDrawer();

  const address = client ? client.addresses.find((addr) => addr.id === id) : undefined;
  const isCurrentlyPrimary = client ? client.primary_address?.id === id : false;

  const [isPrimary, setIsPrimary] = useState(false);
  const { close } = useCurrentDrawer();

  const toggleIsPrimary = () => {
    setIsPrimary((p) => !p);
  };

  if (isClientLoading || isClientUserLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!client || !address || !clientUser) {
    return <DrawerLayout.FetchError entityName="address" error={clientError || clientUserError} />;
  }

  const isPrimaryClientUser = client?.primary_client_user.id === clientUser?.id;

  const relevantSubscriptionCount =
    address.active_subscription_count + address.paused_subscription_count;

  const addressGroup = (
    <DrawerLayout.Form.Group>
      <Box>
        <div className={styles.addressBox}>
          <Address
            address={address}
            styles={{ name: styles.header, address: styles.label, street: styles.label }}
          />
          {isCurrentlyPrimary && <TextChip>Primary</TextChip>}
        </div>
      </Box>
      {!!relevantSubscriptionCount && (
        <TextChip>
          <Icon icon={faArrowsRepeat} className={styles.autoshipIcon} />
          <span>
            {relevantSubscriptionCount}{" "}
            {pluralized(relevantSubscriptionCount, "Autoship ships", "Autoships ship")} here
          </span>
        </TextChip>
      )}
    </DrawerLayout.Form.Group>
  );

  const handleRemoveAddress = () => {
    openRemoveAddressDrawer({
      id,
      address,
      addressGroup,
      isCurrentlyPrimary,
      onSuccess: close,
    });
  };

  return (
    <DrawerLayout>
      <DrawerLayout.Header title="Update address" onClose={close} />
      <DrawerLayout.Form
        submitText="Update Address"
        canSubmit={isPrimary}
        onSubmit={() => updateClient({ primary_address: Number(id) })}
        isLoading={isUpdating}
        error={updateClientError}
        fieldErrors={[updateClientError?.errors.primary_address]}
        hideSubmit={isCurrentlyPrimary}
      >
        {addressGroup}
        {!isCurrentlyPrimary && (
          <DrawerLayout.Form.Group>
            <Tooltip
              isEnabled={!isPrimaryClientUser}
              content="Only primary account owners can set a primary address."
              position="below"
            >
              <Box className={styles.option}>
                <button
                  type="button"
                  onClick={toggleIsPrimary}
                  className={styles.button}
                  disabled={!isPrimaryClientUser}
                >
                  <p className={styles.label}>Set primary address</p>
                </button>
                <div className={styles.switch}>
                  <Switch
                    value={isPrimary}
                    onChange={toggleIsPrimary}
                    disabled={!isPrimaryClientUser}
                  />
                </div>
              </Box>
            </Tooltip>
          </DrawerLayout.Form.Group>
        )}
        <DrawerLayout.Form.Group>
          <Tooltip
            isEnabled={isCurrentlyPrimary}
            content="A primary address is required. To remove this address, first mark another address as primary."
            position="below"
            align="right"
            maxWidth={320}
          >
            <Box
              className={
                isCurrentlyPrimary ? `${styles.option} ${styles.optionDisabled}` : styles.option
              }
            >
              <button
                type="button"
                onClick={handleRemoveAddress}
                className={styles.button}
                disabled={isCurrentlyPrimary}
              >
                <p className={styles.label}>Remove address</p>
                <p className={styles.icon}>
                  <Icon icon={faAngleRight} />
                </p>
              </button>
            </Box>
          </Tooltip>
        </DrawerLayout.Form.Group>
        {isPrimary && (
          <DrawerLayout.Form.Group>
            <p className={styles.label}>
              {client.primary_address
                ? `This will replace ${client.primary_address.street}`
                : "This will be set"}{" "}
              as your primary address. This will be the default for future orders. Updating your
              primary address will not change the shipping address of any Autoships.
            </p>
          </DrawerLayout.Form.Group>
        )}
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default AccountUpdateAddressDrawer;
